/**
 * F-138 storyboard sync — the user's choices and the live state, in ONE
 * module singleton (the F-27 translate store precedent): the top bar's menu
 * toggles it and the board's sync hook reads it, two trees apart.
 *
 * The choices — the ROOT folder and which boards sync — are this machine's,
 * never the doc's (a peer on another laptop has their own folder or none),
 * so they live in localStorage, read defensively like every other preference
 * (features/ai-models/pick.ts): a locked-down browser throws, and a bad
 * value reads as "nothing chosen".
 */
import { createStore, type StoreApi } from "zustand/vanilla";
import { useStore } from "zustand";

const ROOT_KEY = "kola:storyboard-sync:root";
const BOARDS_KEY = "kola:storyboard-sync:boards";

export type StoryboardSyncPhase = "idle" | "syncing" | "synced" | "error";

export interface StoryboardSyncStatus {
  phase: StoryboardSyncPhase;
  /** Epoch ms of the last finished run. */
  at?: number;
  /** The board folder's absolute path, once known. */
  dir?: string;
  /** What went wrong (phase "error"), or a note ("2 files could not be downloaded"). */
  message?: string;
}

export interface StoryboardSyncState {
  rootDir: string | null;
  /** Board ids that sync on this machine. */
  enabled: readonly string[];
  status: Readonly<Record<string, StoryboardSyncStatus>>;
  setRootDir(dir: string | null): void;
  setEnabled(boardId: string, on: boolean): void;
  setStatus(boardId: string, status: StoryboardSyncStatus): void;
}

function readRoot(): string | null {
  try {
    const value = window.localStorage.getItem(ROOT_KEY);
    return value === null || value.length === 0 ? null : value;
  } catch {
    return null;
  }
}

function readBoards(): string[] {
  try {
    const raw = window.localStorage.getItem(BOARDS_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Preference only.
  }
}

export function createStoryboardSyncStore(): StoreApi<StoryboardSyncState> {
  return createStore<StoryboardSyncState>((set, get) => ({
    rootDir: typeof window === "undefined" ? null : readRoot(),
    enabled: typeof window === "undefined" ? [] : readBoards(),
    status: {},
    setRootDir: (rootDir) => {
      write(ROOT_KEY, rootDir);
      set({ rootDir });
    },
    setEnabled: (boardId, on) => {
      const current = get().enabled;
      const next = on
        ? current.includes(boardId)
          ? current
          : [...current, boardId]
        : current.filter((id) => id !== boardId);
      write(BOARDS_KEY, JSON.stringify(next));
      const status = { ...get().status };
      if (!on) delete status[boardId];
      set({ enabled: next, status });
    },
    setStatus: (boardId, status) => {
      set({ status: { ...get().status, [boardId]: status } });
    },
  }));
}

export const storyboardSync: StoreApi<StoryboardSyncState> = createStoryboardSyncStore();

export function useStoryboardSyncEnabled(boardId: string): boolean {
  return useStore(storyboardSync, (s) => s.enabled.includes(boardId));
}

export function useStoryboardSyncStatus(boardId: string): StoryboardSyncStatus | undefined {
  return useStore(storyboardSync, (s) => s.status[boardId]);
}

export function useStoryboardSyncRoot(): string | null {
  return useStore(storyboardSync, (s) => s.rootDir);
}
